import React, { useContext, useEffect } from 'react';
import _merge from 'lodash/merge';
import CheckIcon from '@nokia-csf-uxr/ccfk-assets/CheckIcon';
import classnames from 'classnames';
import { getContext, useCombinedRefs } from 'common';
import useHover from '../common/useHover';
import { ListContext } from './List';
import { CascadingListContext } from './CascadingList';
import { CONSTANTS } from '../common/ListKeyboardNav';
import Styled from './styles';

const {
  LIST_ITEM_TABABLE_ITEM,
  LIST_ITEM_SELECTED,
} = CONSTANTS;

const ListItemCheckedContext = React.createContext<any>({});

export interface ListItemCheckedProps extends React.ComponentPropsWithRef<'div'> {
  /** Display the `checkedIcon` on this list item. */
  checked?: boolean;
  /** The element to use as the checked icon (usually a check). */
  checkedIcon?: React.ReactNode;
  /** Focus this item on mount. */
  autoFocus?: boolean;
  /** Display the list item as disabled. */
  disabled?: boolean;
  /** Callback function that is fired when the list item is selected (by clicking w/mouse or enter/space key via keyboard navigation). */
  onClick?: React.MouseEventHandler;
  /** Callback function that is fired when a key is pressed. */
  onKeyDown?: React.KeyboardEventHandler;
  /** Display the list item as selected. */
  selected?: boolean;
  /** Object indicating styles to be used for the ListItem. */
  style?: React.CSSProperties;
  /** The list items, consider using `ListItemText`, `ListItemIcon`. */
  children: React.ReactNode;
  /**
   * Callback fired when the component gets hovered.
   *
   * @param {object} event The event source of the callback.
   */
  hoverCallback?: () => void;
}

/** ListItemChecked is a ListItem that has may have a leading Icon to indicate selection.
 * All other props will be provided to the wrapper element.
 */
const ListItemChecked = React.forwardRef<HTMLElement, ListItemCheckedProps>((props: ListItemCheckedProps, forwardedRef) => getContext(({ RTL }) => {
  const {
    children,
    autoFocus,
    disabled,
    selected,
    style,
    onKeyDown,
    onClick,
    hoverCallback,
    checked,
    checkedIcon,
    ...otherProps
  } = props;
  const context = useContext(ListContext);
  const cascadingListContext = useContext(CascadingListContext);
  const [hoverRef, isHovered] = useHover();
  const ref = useCombinedRefs(forwardedRef, hoverRef);

  useEffect(() => {
    autoFocus && !disabled && ref && ref.current.focus();
  }, []); // autoFocus on mount only

  useEffect(() => {
    if (isHovered) {
      hoverCallback && hoverCallback();
    }
  }, [isHovered]);
  const handleOnKeyDown = (event) => {
    onKeyDown && onKeyDown(event);
  };

  const handleOnClick = (event) => {
    onClick && onClick(event);
  };
  const classes = classnames({
    [`${LIST_ITEM_TABABLE_ITEM}-${context.uniqueListId}`]: !disabled,
    [LIST_ITEM_SELECTED]: !disabled && selected,
  });
  const resolvedTheme = _merge({}, context.theme, cascadingListContext.theme);
  return (
    <Styled.ListItemChecked
      className={classes}
      aria-disabled={disabled}
      aria-checked={checked}
      aria-selected={selected}
      ref={ref}
      RTL={RTL}
      $theme={resolvedTheme}
      style={style}
      tabIndex={-1}
      disabled={disabled}
      selected={selected}
      hoverEffect={context.hoverEffect}
      onFocus={event => event.preventDefault()}
      onKeyDown={handleOnKeyDown}
      onClick={handleOnClick}
      {...otherProps}
    >
      <Styled.CheckedIndicator RTL={RTL} $theme={resolvedTheme} disabled={disabled}>
        {checked && checkedIcon}
        {!checked && <Styled.NoIndicator />}
      </Styled.CheckedIndicator>
      <ListItemCheckedContext.Provider value={{ disabled, hoverEffect: context.hoverEffect }}>
        {children}
      </ListItemCheckedContext.Provider>
    </Styled.ListItemChecked>
  );
}));

ListItemChecked.displayName = 'ListItemChecked';

ListItemChecked.defaultProps = {
  onClick: undefined,
  onKeyDown: undefined,
  disabled: false,
  selected: false,
  style: undefined,
  autoFocus: false,
  checked: false,
  checkedIcon: <CheckIcon ariaLabel="checked" aria-checked />,
  hoverCallback: undefined
};

export { ListItemChecked as default, ListItemCheckedContext };
